import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, Clock, Star } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Logo } from "./Logo";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL, GOOGLE_BUSINESS_URL, CONTACT_EMAIL } from "@/lib/contact";

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="border-t border-white/10 py-14" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row justify-between gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Logo size={32} />
            <span className="font-display font-semibold tracking-tight">
              Smart <span className="text-[#FF7900]">Telecom</span>{" "}Advisor
            </span>
          </div>
          <p className="text-sm text-white/50 max-w-xs">{t.footer.tagline}</p>
          <div className="flex items-center gap-3 mt-5">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`${t.referral.shareText} ${window.location.origin}`)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-share-whatsapp"
              aria-label={t.referral.shareWhatsapp}
              className="w-9 h-9 border border-white/15 rounded-full flex items-center justify-center text-white/50 hover:text-[#25D366] hover:border-[#25D366]/60 transition-colors duration-300"
            >
              <MessageCircle className="w-4 h-4" />
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent(t.referral.shareSubject)}&body=${encodeURIComponent(`${t.referral.shareText}\n${window.location.origin}`)}`}
              data-testid="footer-share-email"
              aria-label={t.referral.shareEmail}
              className="w-9 h-9 border border-white/15 rounded-full flex items-center justify-center text-white/50 hover:text-[#FF7900] hover:border-[#FF7900]/60 transition-colors duration-300"
            >
              <Mail className="w-4 h-4" />
            </a>
          </div>
          <div className="mt-5 flex flex-col items-start gap-3">
            <div className="bg-white rounded-sm border border-white/10 px-3 py-2" data-testid="footer-smart-sn-logo">
              <img src="/smart-sn-logo.webp" alt="Smart Services Network" className="w-40" />
            </div>
            <img
              src="/orange-distributor.png"
              alt="Distributeur Officiel Orange"
              className="w-14 rounded-sm border border-white/10"
              data-testid="footer-distributor-badge"
            />
            <div className="bg-white rounded-sm border border-white/10 px-3 py-1.5 inline-flex items-center gap-1.5" data-testid="footer-eneco-logo">
              <img src="/eneco-bubbles.png" alt="Eneco" className="h-6" />
              <span className="font-bold text-lg leading-none" style={{ color: "#e2001a" }}>Eneco</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col md:items-end gap-3">
          <a
            href={PHONE_TEL}
            data-testid="footer-phone-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-[#FF7900] transition-colors duration-300"
          >
            <Phone className="w-4 h-4" />
            {PHONE_DISPLAY}
          </a>
          <a
            href="/blog"
            data-testid="footer-blog-link"
            className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-[#FF7900] transition-colors duration-300"
          >
            {t.nav.blog}
          </a>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="footer-email-link"
            className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-[#FF7900] transition-colors duration-300"
          >
            <Mail className="w-4 h-4" />
            {CONTACT_EMAIL}
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-whatsapp-link"
            className="text-sm text-white/50 hover:text-[#25D366] transition-colors duration-300"
          >
            WhatsApp
          </a>
          <Link
            to="/login"
            data-testid="footer-agent-link"
            className="text-sm text-white/50 hover:text-[#FF7900] transition-colors duration-300"
          >
            {t.footer.agent}
          </Link>
          <p className="inline-flex items-center gap-2 text-sm text-white/50" data-testid="footer-hours">
            <Clock className="w-4 h-4" />
            {t.footer.hours}
          </p>
          {GOOGLE_BUSINESS_URL && (
            <a
              href={GOOGLE_BUSINESS_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-google-business"
              className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-[#FBBC04] transition-colors duration-300"
            >
              <Star className="w-4 h-4" />
              {t.footer.googleReviews}
            </a>
          )}
          <p className="text-xs text-white/30">{t.footer.rights}</p>
          <p className="text-xs text-white/25 max-w-sm md:text-right">{t.footer.disclaimer}</p>
        </div>
      </div>
    </footer>
  );
};
